import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahShifts } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { CompanyShift } from '~lookups/models';

export const typePrefix = 'company-shifts';

/** One band in a bulk save. Precedence is deliberately absent - the API writes the platform value. */
export interface CompanyShiftInput {
  shift_id: string;
  start_time: string;
  end_time: string;
}

export interface CompanyShiftsSaveRequest {
  shifts: CompanyShiftInput[];
}

/**
 * What the save did. `cleared` counts the override rows removed because the submitted times matched
 * the platform band, which is how a company goes back to tracking the platform default.
 */
export interface CompanyShiftsSaveResult {
  created: number;
  updated: number;
  cleared: number;
}

/** Codes the API returns against a rejected band. */
export type CompanyShiftErrorCode =
  | 'UNKNOWN_SHIFT'
  | 'MISSING_SHIFT'
  | 'DUPLICATE_SHIFT'
  | 'INVALID_TIME'
  | 'ALL_DAY_NOT_ALLOWED'
  | 'ZERO_LENGTH'
  | 'COVERAGE_GAP'
  | 'COVERAGE_OVERLAP';

export interface CompanyShiftError {
  shift_id?: string;
  tag?: string;
  code: CompanyShiftErrorCode;
  message: string;
}

/** Body of the 400 the API returns when a submitted set breaks any band rule. */
export interface CompanyShiftsErrorResponse {
  success: false;
  message: string;
  errors?: CompanyShiftError[];
}

export const companyShiftsApi = createApi({
  reducerPath: 'companyShiftsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahShifts }),
  tagTypes: ['company-shifts'],
  endpoints: (builder) => ({
    getCompanyShifts: builder.query<CompanyShift[], void>({
      query: () => ({ url: '', method: 'GET' }),
      providesTags: [{ type: 'company-shifts', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CompanyShift[]>(payload),
    }),
    // Atomic save of the company's whole editable band set. Either every band is accepted or the
    // request is rejected in full with a per-band reason.
    saveCompanyShifts: builder.mutation<CompanyShiftsSaveResult, CompanyShiftsSaveRequest>({
      query: (body) => ({ url: '', method: 'PUT', body }),
      invalidatesTags: [{ type: 'company-shifts', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CompanyShiftsSaveResult>(payload),
    }),
  }),
});

export const { useGetCompanyShiftsQuery, useSaveCompanyShiftsMutation } = companyShiftsApi;
